import { api } from "shared/redux/api"
import { buildListParams, buildProcurementListFilters } from "./procurement"
import { camelizeKeys } from "shared/utils/camelize"

// Procurement Manager — Payment Request (PR) module.
//
// Last leg of P2P: OR → PO → GRN → PR → SUN (passive). Mirrors
// procurementPurchases.js: hits the REAL backend under `/private/procurement/v1/*`
// (the axios base query attaches the bearer token only when the URL contains
// "private"), unwraps the `{status/code, message, data}` envelope, and
// camelCases snake_case keys once here so every consumer reads flat camelCase
// fields (`allowedActions`, `paidBatches`, `paymentCode`, `approvalProgress`,
// `sunPaymentStatus`, …).
//
// PR lifecycle (BE is source of truth for buttons — render from allowedActions):
//   create(draft) --submit--> pending --approve--> approved --send_to_accounting-->
//   sent_to_accounting --(SUN cron)--> paid
//                  \info_request --> info_requested --resubmit--> pending
//                  \reject --> rejected
//
// FIELD NAME NOTE (verify on first real hit): the endpoint spec lists
// `allowedActions` (camelCase) while the BA contract wrote `allowed_actions`
// (snake_case). Both land as `allowedActions` after `camelizeKeys`, so the
// consumer contract stays identical either way. If the field is missing
// entirely from the response, `allowedActionsFor(status)` below fills it in
// client-side; a `draft` row additionally always gets "submit" forced back in
// even when the field IS present but happens to omit it — see
// `withAllowedActionsCompat` — so the F5 action bar never renders empty for a
// draft. Remove this once BE confirms it always sends "submit" for drafts.
const BASE = "/private/procurement/v1"
const LIST_PAGE_SIZE = 100

const unwrap = (response) => camelizeKeys(response?.data)

// Mirrors pages/procurement/status.js's PAYMENT_REQUEST_STATUS — the
// redux/endpoints layer doesn't import from pages/.
const PAYMENT_REQUEST_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  INFO_REQUESTED: "info_requested",
  APPROVED: "approved",
  REJECTED: "rejected",
  SENT_TO_ACCOUNTING: "sent_to_accounting",
  PAID: "paid",
}

// Buttons on the PR Detail screen render ENTIRELY from allowedActions — never
// inferred from status client-side. This is a COMPAT fallback: if the real
// response drops the field, derive it from status so F5 doesn't render an
// empty action bar. Drop this once BE contract is verified.
const allowedActionsFor = (status) => {
  switch (status) {
    case PAYMENT_REQUEST_STATUS.DRAFT:
      return ["submit"]
    case PAYMENT_REQUEST_STATUS.PENDING:
      return ["approve", "reject", "info_request"]
    case PAYMENT_REQUEST_STATUS.INFO_REQUESTED:
      return ["resubmit"]
    case PAYMENT_REQUEST_STATUS.APPROVED:
      return ["send_to_accounting"]
    default:
      return []
  }
}

// A `draft` PR is always submittable (create --submit--> pending is the one
// universal first step) — so even when the field IS present but comes back
// empty (or just missing "submit") for a draft row, add it back rather than
// trusting that as "no actions available".
const withAllowedActionsCompat = (row) => {
  if (!row || typeof row !== "object") return row
  const actions = Array.isArray(row.allowedActions) ? row.allowedActions : allowedActionsFor(row.status)
  if (row.status === PAYMENT_REQUEST_STATUS.DRAFT && !actions.includes("submit")) {
    return { ...row, allowedActions: [...actions, "submit"] }
  }
  return actions === row.allowedActions ? row : { ...row, allowedActions: actions }
}

// Single-PR responses (get / create / update) nest the record under `header`
// — same shape as the Order/PO detail endpoints (`header` + `approvalProgress[]`
// + `actionLog[]` + `allowedActions[]`, see the GET comment below) — but this
// was never flattened here like it is for Order/PO. Every `pr.id`/`pr.status`/
// `pr.paymentCode`/... read was silently getting `undefined`, masked by the
// `|| []` / `|| "Detail"` fallbacks sprinkled through the detail page — until
// it surfaced as a literal "undefined" in the `.../payment-requests/{id}/submit`
// URL right after create (`created.id` was undefined; the id was actually at
// `created.header.id`). No-op if a response ever arrives already flat.
const flattenPaymentRequestHeader = (data) => {
  if (!data || !data.header) return data
  const { header, ...rest } = data
  return { ...rest, ...header, id: header.id }
}

const unwrapPaymentRequest = (response) =>
  withAllowedActionsCompat(flattenPaymentRequestHeader(unwrap(response)))

const unwrapPaymentRequestList = (response) => {
  const data = unwrap(response)
  if (Array.isArray(data)) return { items: data.map(withAllowedActionsCompat) }
  if (Array.isArray(data?.items)) return { ...data, items: data.items.map(withAllowedActionsCompat) }
  if (Array.isArray(data?.content)) return { ...data, items: data.content.map(withAllowedActionsCompat) }
  return { items: [] }
}

const unwrapPaymentContext = (response) => {
  const data = unwrap(response)
  if (!data) return data
  return {
    ...data,
    paidBatches: (data.paidBatches || []).map(withAllowedActionsCompat),
  }
}

// Request-body shape shared by create (A3) and update (A4): the endpoint spec
// nests bank/invoice fields under `bank: {}` / `invoice: {}`, unlike the flat
// header fields the GET responses return — so this maps one line's flat FE
// state into the nested wire shape expected on write.
const toBankInvoiceBody = (line) => ({
  paymentType: line.paymentType,
  amount: Number(line.amount) || 0,
  batchLabel: line.batchLabel || null,
  paymentTimeline: line.paymentTimeline || null,
  bank: {
    bankReceiverName: line.bankReceiverName || null,
    bankNameBranch: line.bankNameBranch || null,
    bankAccount: line.bankAccount || null,
  },
  invoice: {
    invoiceSerial: line.invoiceSerial || null,
    invoiceNumber: line.invoiceNumber || null,
    invoiceDate: line.invoiceDate || null,
    supplierTaxCode: line.supplierTaxCode || null,
  },
  description: line.description || null,
  note: line.note || null,
})

// Two containers pass an already-fetched object (purchase / order) rather than
// a bare id, since the pre-real mock derived response fields from that object.
// Real API only needs the id — accept either shape and pull it out.
const idOf = (arg) => (arg && typeof arg === "object" ? arg.id : arg)

// After any lifecycle mutation the detail + list must both refresh so the
// status badge, stepper and allowedActions re-render from the server truth.
// The purchase's payment-context also re-runs (remaining balance changed) and
// any order-lifecycle card refreshes to pick up the new batch state. A PR
// action can also flip its linked Purchase's payable/paid state (e.g. send
// to accounting), which is what the To Pay list (getPurchases with
// payable:true) filters on — invalidate the PURCHASE list too so a To Pay
// page opened before this action doesn't keep showing a PO that no longer
// belongs there. These mutations don't carry a purchaseId, so this can only
// invalidate the list broadly, not one specific {PURCHASE, id} — acceptable
// since the open problem was stale LIST pages, not a stale detail tab.
const invalidatePaymentRequest = (_r, _e, arg) => [
  { type: "PAYMENT_REQUEST", id: idOf(arg) },
  { type: "PAYMENT_REQUEST", id: "LIST" },
  { type: "ORDER-LIFECYCLE", id: "ALL" },
  { type: "PURCHASE", id: "LIST" },
]

const procurementPaymentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // GET /payment-requests?...filters — org-wide PR list (F4), also reused
    // filtered by purchaseId for F1's "paid batches" / F2's context.
    // Wire params are snake_case for the multi-word ones (po_code,
    // period_from, period_to, awaiting_me) — this maps from the camelCase
    // the FE uses everywhere else so callers don't have to think about it.
    // `awaitingMe: true` makes the backend ignore every other filter and
    // return only PRs where the caller is the current-step approver.
    // periodFrom/periodTo (invoice_date) are kept here for callers that still
    // want to filter by invoice date, but the F4 list UI now filters by
    // fromDate/toDate (created_at, via buildProcurementListFilters) instead —
    // see PaymentRequestsContainer.
    // BE pages this list (default size 20, created_at ASC) but the screens
    // search/sort/paginate client-side, so every page is fetched and merged.
    getPaymentRequests: build.query({
      queryFn: async ({ awaitingMe, poCode, periodFrom, periodTo, ...rest } = {}, _api, _extra, baseQuery) => {
        const params = buildProcurementListFilters({
          ...rest,
          po_code: poCode,
          period_from: periodFrom,
          period_to: periodTo,
          awaiting_me: awaitingMe,
        })
        const items = []
        for (let page = 0; ; page += 1) {
          const res = await baseQuery({ url: `${BASE}/payment-requests`, params: { ...params, page, size: LIST_PAGE_SIZE } })
          if (res.error) return { error: res.error }
          const data = unwrapPaymentRequestList(res.data)
          items.push(...data.items)
          if (data.total == null || data.items.length === 0 || items.length >= data.total) {
            return { data: { ...data, items, total: data.total ?? items.length } }
          }
        }
      },
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((p) => ({ type: "PAYMENT_REQUEST", id: p.id })),
              { type: "PAYMENT_REQUEST", id: "LIST" },
            ]
          : [{ type: "PAYMENT_REQUEST", id: "LIST" }],
    }),

    // GET /payment-requests/{id} — header + approvalProgress[] + actionLog[]
    // + allowedActions[] (F5).
    getPaymentRequest: build.query({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}` }),
      transformResponse: unwrapPaymentRequest,
      providesTags: (_r, _e, id) => [{ type: "PAYMENT_REQUEST", id }],
    }),

    // GET /payment-requests/line-capacity?purchaseId=&amount=&excludePaymentRequestId=
    // Per OR line of one PO: `remaining` (max this PR may put on it) and BE's
    // default split of `amount` (`suggestedAmount`, `suggestedExtraAmount`).
    getPaymentRequestLineCapacity: build.query({
      query: ({ purchaseId, amount, excludePaymentRequestId }) => ({
        url: `${BASE}/payment-requests/line-capacity`,
        params: { purchaseId, amount, excludePaymentRequestId: excludePaymentRequestId || undefined },
      }),
      transformResponse: unwrap,
      providesTags: [{ type: "PAYMENT_REQUEST", id: "LIST" }],
    }),

    // GET /payment-requests/{id}/budget-allocations — one row per (PO, OR line),
    // PO-linked and Non-PO alike; `value` is BE's split of the PO payment.
    getPaymentRequestBudgetAllocations: build.query({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}/budget-allocations` }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return Array.isArray(data) ? data : []
      },
      providesTags: (_r, _e, id) => [{ type: "PAYMENT_REQUEST", id }],
    }),

    // GET /payment-requests/{id}/budget-preview — Non-PO only (409 for a PR
    // with a PO). One entry per center of the PR's lines; `budgetImpact` is
    // PREVIEW (draft) / COMMITTED (submitted onwards) / RELEASED (rejected/deleted).
    getPaymentRequestBudgetPreview: build.query({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}/budget-preview` }),
      transformResponse: (response) => {
        const data = unwrap(response)
        return Array.isArray(data) ? data : []
      },
      providesTags: (_r, _e, id) => [{ type: "PAYMENT_REQUEST", id }],
    }),

    // GET /purchases/{id}/payment-context — remaining balance + already
    // created batches + default bank for one Purchase (F2). Accepts either a
    // bare id or the pre-fetched purchase object (containers still pass the
    // object from the pre-real mock era) — only the id is sent to BE.
    getPurchasePaymentContext: build.query({
      query: (arg) => {
        const id = idOf(arg)
        if (!id) throw new Error("Missing purchase id")
        return { url: `${BASE}/purchases/${id}/payment-context` }
      },
      transformResponse: unwrapPaymentContext,
      providesTags: (_r, _e, arg) => [{ type: "PAYMENT_REQUEST", id: `CTX-${idOf(arg)}` }],
    }),

    // POST /payment-requests — create exactly ONE Draft payment_request row
    // for one Purchase. F3's multi-batch form calls this once per line
    // (looped client-side) since the endpoint itself takes no lines[].
    //
    // `directOrder` + `vendorId` (2026-09 barrier-removal feature): an
    // alternative to `purchaseId` — {title, budgetCategoryId, centerId,
    // departmentId?, region?, reason?, lines[]} that BE turns into a hidden
    // "implicit order" + a hidden, already-`issued` PO, then links this PR to
    // that PO same as usual. Requires the chosen category (and any itemId'd
    // line) to have mustHavePurchase=false, else 409; `amount` must equal the
    // sum of the lines' quantity×unitPrice or BE 409s.
    //
    // `newSupplier` ({name, taxCode, email?}, 2026-09) — alternative to
    // `vendorId`, only meaningful alongside `directOrder` (a PO-linked PR's
    // vendor comes from the PO itself). BE creates the supplier inline, same
    // as Create Purchase's `newSupplier`.
    createPaymentRequest: build.mutation({
      // `links[]` ({ purchaseId, allocatedAmount, lines? }) lets one PR span several
      // purchases. With links, BE 400s on `purchaseId`/`directOrder`, duplicate
      // purchaseIds, or `amount` ≠ sum of allocatedAmount.
      query: ({ purchaseId, directOrder, vendorId, newSupplier, links, ...line }) => ({
        url: `${BASE}/payment-requests`,
        method: "POST",
        body: {
          purchaseId,
          directOrder,
          vendorId,
          newSupplier,
          ...(links ? { links } : {}),
          ...toBankInvoiceBody(line),
        },
      }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: (_r, _e, { purchaseId, links }) => [
        { type: "PAYMENT_REQUEST", id: "LIST" },
        ...(links ? links.map((l) => l.purchaseId) : [purchaseId]).map((pid) => ({ type: "PAYMENT_REQUEST", id: `CTX-${pid}` })),
        { type: "ORDER-LIFECYCLE", id: "ALL" },
        { type: "PURCHASE", id: "LIST" },
      ],
    }),

    // PUT /payment-requests/{id} — edit a Draft batch before submitting. BE
    // returns 409 if the row has left Draft; F3 also disables the Edit route
    // upstream so a stale tab can't slip a PUT through silently.
    updatePaymentRequest: build.mutation({
      // Non-PO: `directOrder` must be resent on every PUT (lines are replaced).
      // PO-linked: `lines` (ITEM/EXTRA, each with purchaseId) must list every
      // PO again — a PUT without it resets BE to the default split.
      query: ({ id, directOrder, lines, ...line }) => ({
        url: `${BASE}/payment-requests/${id}`,
        method: "PUT",
        body: { ...(directOrder ? { directOrder } : {}), ...(lines ? { lines } : {}), ...toBankInvoiceBody(line) },
      }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // DELETE /payment-requests/{id} — remove a Draft before it's submitted.
    // 204 No Content on success.
    deletePaymentRequest: build.mutation({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PAYMENT_REQUEST", id },
        { type: "PAYMENT_REQUEST", id: "LIST" },
        { type: "ORDER-LIFECYCLE", id: "ALL" },
        { type: "PURCHASE", id: "LIST" },
      ],
    }),

    // POST /payment-requests/{id}/documents — multipart `file` + `docType`
    // (invoice_xml | invoice_pdf | receipt | other). Draft/info_requested only
    // (409 otherwise); xml/pdf/jpg/png/xlsx/docx up to 10MB (400 otherwise).
    // Response is the full PR with the file appended to `documents[]`.
    uploadPaymentRequestDocument: build.mutation({
      query: ({ id, file, docType }) => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("docType", docType)
        return { url: `${BASE}/payment-requests/${id}/documents`, method: "POST", body: formData }
      },
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // DELETE /payment-requests/{id}/documents/{documentId} — same status gate
    // as upload; 404 if the document isn't this PR's. Only the DB row goes —
    // the S3 object is kept.
    deletePaymentRequestDocument: build.mutation({
      query: ({ id, documentId }) => ({
        url: `${BASE}/payment-requests/${id}/documents/${documentId}`,
        method: "DELETE",
      }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // POST /payment-requests/{id}/links — merge another Purchase's balance
    // into an existing (Draft) PR. Body: { purchaseId, allocatedAmount }.
    linkPurchaseToPaymentRequest: build.mutation({
      query: ({ id, purchaseId, allocatedAmount }) => ({
        url: `${BASE}/payment-requests/${id}/links`,
        method: "POST",
        body: { purchaseId, allocatedAmount },
      }),
      invalidatesTags: (_r, _e, { id, purchaseId }) => [
        { type: "PAYMENT_REQUEST", id },
        { type: "PAYMENT_REQUEST", id: `CTX-${purchaseId}` },
        { type: "ORDER-LIFECYCLE", id: "ALL" },
        { type: "PURCHASE", id: "LIST" },
      ],
    }),

    // POST /payment-requests/{id}/submit — Draft → Pending, or straight to
    // SentToAccounting when the resolved approval rule requires no approval.
    submitPaymentRequest: build.mutation({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}/submit`, method: "POST" }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // POST /payment-requests/{id}/actions — approver decision, shared with the
    // same Approval Rule Engine service as OR/PO (no bespoke approval logic
    // here). Body: { action, note, deadline } where action ∈ approve | reject |
    // info_request. `deadline` is only meaningful for info_request: optional,
    // "yyyy-MM-dd" (BE defaults to end of day VN time) or
    // "yyyy-MM-ddTHH:mm" for an exact cutoff — omitted entirely when not set.
    // Only offered when present in allowedActions[].
    paymentRequestAction: build.mutation({
      query: ({ id, action, note, deadline }) => ({
        url: `${BASE}/payment-requests/${id}/actions`,
        method: "POST",
        body: { action, note, deadline },
      }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // POST /payment-requests/{id}/resubmit — InfoRequested → Pending.
    resubmitPaymentRequest: build.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/payment-requests/${id}/resubmit`,
        method: "POST",
        body: { note },
      }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // POST /payment-requests/{id}/send-to-accounting — Approved →
    // SentToAccounting. "Paid" itself is never a manual action — it's set by
    // the passive BE cron that scans SUN and matches this record.
    sendPaymentRequestToAccounting: build.mutation({
      query: (id) => ({ url: `${BASE}/payment-requests/${id}/send-to-accounting`, method: "POST" }),
      transformResponse: unwrapPaymentRequest,
      invalidatesTags: invalidatePaymentRequest,
    }),

    // GET /order-requests/{id}/lifecycle — end-to-end P2P tracking for one
    // order request, read-only (F6 "My Orders" Payment stage). Accepts either
    // a bare id or the pre-fetched order object — only the id is sent to BE.
    // The Payment card in F6 depends on BE returning `purchases[].batches` in
    // this response; if that field is still TODO on BE, the card renders empty
    // and no error is thrown.
    getOrderLifecycle: build.query({
      query: (arg) => {
        const id = idOf(arg)
        if (!id) throw new Error("Missing order id")
        return { url: `${BASE}/order-requests/${id}/lifecycle` }
      },
      transformResponse: unwrap,
      providesTags: (_r, _e, arg) => [{ type: "ORDER-LIFECYCLE", id: idOf(arg) || "ALL" }],
    }),

    // GET /payment-requests/approval/preview?category=&amount=&region=&center=
    // — resolves the approval rule/steps a PR of this shape would follow,
    // without creating anything. Used by F3 to show the approver up front.
    getPaymentRequestApprovalPreview: build.query({
      query: (params = {}) => ({
        url: `${BASE}/payment-requests/approval/preview`,
        params: buildListParams(params),
      }),
      transformResponse: unwrap,
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPaymentRequestsQuery,
  useGetPaymentRequestQuery,
  useLazyGetPaymentRequestQuery,
  useGetPaymentRequestBudgetAllocationsQuery,
  useGetPaymentRequestLineCapacityQuery,
  useLazyGetPaymentRequestBudgetAllocationsQuery,
  useGetPaymentRequestBudgetPreviewQuery,
  useLazyGetPaymentRequestBudgetPreviewQuery,
  useGetPurchasePaymentContextQuery,
  useCreatePaymentRequestMutation,
  useUpdatePaymentRequestMutation,
  useDeletePaymentRequestMutation,
  useUploadPaymentRequestDocumentMutation,
  useDeletePaymentRequestDocumentMutation,
  useLinkPurchaseToPaymentRequestMutation,
  useSubmitPaymentRequestMutation,
  usePaymentRequestActionMutation,
  useResubmitPaymentRequestMutation,
  useSendPaymentRequestToAccountingMutation,
  useGetOrderLifecycleQuery,
  useGetPaymentRequestApprovalPreviewQuery,
} = procurementPaymentsApi

export default procurementPaymentsApi
